import {css, html, LitElement} from "lit";

import {Position} from "geojson";
import {pxToRem} from "../../styles";
import '../marker.component'
import {MapClusterMarker, MapMarker} from "../marker.component";
import {MaplibreClient} from "./maplibre.client";
import {MapConfig} from "../MapConfig";
import {PropertyValues} from "@lit/reactive-element";
import {emptyMapPoint, MapPoint} from "../MapPoint";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {MAP_BUTTON_CLICKED_EVENT_TYPE} from "../../../tracking/constants";
import * as tracking from "../../../core/tracking";

const POPUP_CLASS = 'custom-popup';

export class Serpmap extends LitElement {
    private accessToken: string;
    private mapClient: MaplibreClient | undefined;
    private zoom: number;
    private longitude: number;
    private latitude: number;
    private loading: boolean = false;
    private fixedPopup: boolean = false;
    private mapElement: HTMLElement | undefined;
    private legendApproximate: string;
    private legendMultipleProperties: string;
    private readonly mapTrackingProperties: string;

    getPoints: () => Promise<GeoJSON.GeoJSON> = () => Promise.resolve({'type': 'FeatureCollection', features: []})
    renderSnippet: (map: MapPoint) => string = () => ""
    renderLoading: () => string = () => "Loading..."
    getDetail: (listingId: string) => Promise<MapPoint> = (listingId: string) => Promise.resolve(emptyMapPoint())

    public constructor() {
        super();
        this.accessToken = "";
        this.zoom = 1;
        this.longitude = 0;
        this.latitude = 0;
        this.loading = false;
        this.legendApproximate = "Ubicación aproximada";
        this.legendMultipleProperties = "Múltiples propiedades";
        this.mapTrackingProperties = "{}";
    }

    static get properties() {
        return {
            longitude: {type: Number},
            latitude: {type: Number},
            zoom: {type: Number},
            getPoints: {type: Function},
            accessToken: {type: String, attribute: 'access-token'},
            loading: {type: Boolean},
            fixedPopup: {type: Boolean, attribute: 'fixed-popup'},
            legendApproximate: {type: String, attribute: 'legend-approximate'},
            legendMultipleProperties: {type: String, attribute: 'legend-multiple-properties'},
            mapTrackingProperties: {type: String, attribute: 'tracking-properties'},
        }
    }

    static styles = [
        css`
            :host {
                height: 100%;
                width: 100%;
                display: block;
                position: relative;
            }

            .fixed-popup .maplibregl-popup-tip {
                display: none;
            }

            .maplibregl-popup, .custom-popup {
                z-index: 20;
            }
            
            .fixed-popup .maplibregl-popup, .custom-popup {
                top: 10px;
                left: 50%;
                transform: none;
            }

            .maplibregl-popup-content, .custom-popup-content {
                width: 100%;
                flex-direction: column;
                box-shadow: 0 0.0625rem 0.375rem 0rem rgba(33, 37, 41, 0.25);
                border-radius: 0.625rem;
                gap: 0;
                overflow: visible;
                padding: 0;
            }

            .custom-popup-content {
                width: var(--serp-map-custom-popup-width, 300px);
                background-color: white;
                position: absolute;
                top: var(--serp-map-custom-popup-top, 10px);
                left: var(--serp-map-custom-popup-left, 10px); 
                z-index: 20;
            }

            #map {
                height: 100%;
                width: 100%;
            }

            .legend {
                height: ${pxToRem(62)};
                gap: 1px;
                border-radius: 10px;
                display: flex;
                flex-direction: row;

                position: absolute;
                bottom: ${pxToRem(15)};
                right: ${pxToRem(20)};

                background-color: #F4F4F4;
                box-shadow: 0px 4px 8px 0px #00000040;
            }

            .legend:not(:last-child) {
                border-right: 1px solid #ADA5AD1A;
            }

            .legend-item {
                display: flex;
                flex-direction: row;
                padding: ${pxToRem(10)} ${pxToRem(8)};
                max-width: ${pxToRem(140)};
                gap: 10px;
            }

            .legend-item > span {
                font-family: Open Sans;
                font-weight: 400;
                font-size: ${pxToRem(11)};
                vertical-align: middle;
            }

            .legend-item lamudi-map-marker {
                margin: ${pxToRem(10)};
            }

            .legend-item {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .maplibregl-ctrl-group {
                position: absolute;
                right: ${pxToRem(10)};
                bottom: ${pxToRem(75)};
            }

            .loader-wrapper {
                width: 100%;
                height: 100%;
                background-color: rgba(255, 255, 255, 0.4);
                position: absolute;
                top: 0;
                left: 0;
            }

            .loader {
                width: 100px;
                height: 50px;

                background-color: #F4F4F4;
                box-shadow: 0px 4px 8px 0px #00000040;

                border-radius: 10px;

                position: absolute;
                bottom: 50%;
                right: calc(50% - 50px);

                display: flex;
                align-items: center;
                justify-content: space-around;
            }

            .hide {
                display: none;
            }

            @media only screen and (min-width: 760px) {
                .loader {
                    bottom: calc(50% - 25px);
                }
            }
        `
    ]

    private manageClickOnMarker(event: Event, coordinates: Position, listingId: string) {
        if (event.currentTarget instanceof Element) {
            if ((event.currentTarget as Element) instanceof MapClusterMarker) {
                this.mapClient?.zoomOn(coordinates);
            } else {
                this.getDetail(listingId).then((point) => {
                    if (this.fixedPopup) {
                        this.removeCustomPopup()
                        this.createCustomPopup(`${this.renderSnippet(point)}`);
                    } else {
                        this.mapClient?.createPopup(point, this.renderSnippet(point));
                    }
                })
            }
        }
    }

    private createCustomPopup(popupContent: string) {
        let div = document.createElement('div')
        div.classList.add(POPUP_CLASS)
        div.innerHTML = `<div class="custom-popup-content">
            ${popupContent}
        </div>`
        this.mapElement?.appendChild(div)
    }

    private removeCustomPopup() {
        this.mapElement?.querySelector(`.${POPUP_CLASS}`)?.remove()
    }

    show() {
        this.mapElement = this.shadowRoot!!.querySelector<HTMLElement>('#map')!!
        if (this.fixedPopup) {
            this.mapElement.classList.add('fixed-popup');
        }
        setTimeout(() => this.mapClient?.init(this.mapElement!!), 100);

        this.trackClickEvent();
    }

    private createListingPoint(priceTag: string, id: string, exactLocation: boolean): HTMLElement {
        const marker = document.createElement("wl-map-marker") as MapMarker;
        marker.label = priceTag;
        marker.listingId = id;
        if (!exactLocation) {
            marker.approximate = true;
        }
        return marker
    }

    private createClusterPoint(total: number): HTMLElement {
        const marker = document.createElement('wl-map-cluster-marker') as MapClusterMarker;
        marker.total = total;
        return marker
    }


    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        this.mapClient = new MaplibreClient(
            new MapConfig(this.latitude, this.longitude, this.zoom, this.accessToken),
            this.createListingPoint.bind(this),
            this.createClusterPoint.bind(this),
            ((loading: boolean) => {
                this.loading = loading
            }),
            this.manageClickOnMarker.bind(this),
            ((zoomLevel: number) => {
                this.shadowRoot!!
                    .querySelectorAll<MapMarker>('wl-map-marker')
                    .forEach((marker) => {
                        if (!marker.classList.contains("inlegend")) {
                            marker.showLabel = zoomLevel > 14
                        }
                    })
            }),
            (() => {
                this.getPoints().then((points) => this.mapClient?.loadPoints(points))
            }),
            () => { this.removeCustomPopup(); },
        );
    }


    render() {
        return html`
            <link href="https://unpkg.com/maplibre-gl@^5.2.0/dist/maplibre-gl.css" rel="stylesheet"/>
            <div id="map"></div>
            <div class="loader-wrapper ${(this.loading) ? '' : 'hide'}">
                <div class="loader">
                    ${unsafeHTML(this.renderLoading())}
                </div>
            </div>
            <div class="legend">
                <div class="legend-item">
                    <wl-map-marker approximate class="inlegend"></wl-map-marker>
                    <span>${this.legendApproximate}</span>
                </div>
                <div class="legend-item">
                    <wl-map-cluster-marker total="2"></wl-map-cluster-marker>
                    <span>${this.legendMultipleProperties}</span>
                </div>
            </div>
        `
    }

    private trackClickEvent() {
        let type: string = MAP_BUTTON_CLICKED_EVENT_TYPE;
        let mapTrackingProperties = JSON.parse(this.mapTrackingProperties) as MapTrackingProperties;
        tracking.trackWebTrackingEvent(
            {
                type: type,
                serpUrl: window.location.href,
                section: mapTrackingProperties.sectionId,
                pageViewId: mapTrackingProperties.pageViewId,
                sectionName: mapTrackingProperties.sectionName,
            }
        );
    }
}

export interface MapTrackingProperties {
    sectionId: string;
    pageViewId: string;
    sectionName: string;
}


customElements.define('wl-serp-map', Serpmap);
